/* eslint-disable max-lines */

import { ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';

import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AccountSelectors } from '@recruitee/account-data';
import type { User } from '@recruitee/account-types';
import { BillingPlansSelectors } from '@recruitee/billing-plans-data';
import { ChooseYourPlanContext, SampleDataAnalyticsService } from '@recruitee/company-logic';
import type { Company } from '@recruitee/company-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { InvoiceType } from '@recruitee/invoices-types';
import type { BillingPlan, CustomBillingPlan } from '@recruitee/products-types';
import {
  diffDates,
  AdminsAvatarsComponent,
  IconComponent,
  IconColorDirective,
  ButtonComponent,
  HeadingComponent,
  BoxComponent,
  SpinnerComponent,
} from '@recruitee/user-interface';

import { ready } from '../../helpers/rx-operators';
import { AssignedProductService } from '../../services/assigned-product.service';
import { AssignedProductMessageContainerComponent } from '../../shared/assigned-product-message/assigned-product-message-container.component';
import {
  PricingFeatureFacade,
  PricingFeatureLoadedModel,
  PricingFeatureModel,
  PricingFeatureObservableModel,
} from '../../types/pricing-feature.types';
import { BillingInvoicesComponent } from '../billing-page/billing-invoices/billing-invoices.component';

type FaqItem = {
  questionKey: string;
  answerKey: string;
};

type BillingLockedStateReason =
  | 'trial_active'
  | 'trial_ended'
  | 'subscription_expired'
  | 'subscription_blocked';

@Component({
  selector: 'rt-billing-locked-page',
  templateUrl: './billing-locked-page.component.html',
  styleUrls: ['./billing-locked-page.component.less'],
  imports: [
    AssignedProductMessageContainerComponent,
    BoxComponent,
    HeadingComponent,
    ButtonComponent,
    IconColorDirective,
    IconComponent,
    AdminsAvatarsComponent,
    BillingInvoicesComponent,
    SpinnerComponent,
    TranslatePipe,
  ],
})
export class BillingLockedPageComponent {
  public model: PricingFeatureModel = {
    loading: true,
    loaded: false,
  };

  public hasAssignedProduct: boolean = false;
  public teamMembers: User[];

  public showCta: boolean;
  public showFaq: boolean;
  public showAdminsAvatars: boolean;

  public title: string;
  public message: string;
  public cta: string;
  public imageUrl: string;

  public expandedQuestions = new Set<FaqItem>();
  public commonQuestions: FaqItem[];

  public trialDaysLeft: number = 0;

  public invoices: InvoiceType[];
  public hasMoreInvoices: boolean;

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    public facade: PricingFeatureFacade,
    private model$: PricingFeatureObservableModel,
    private assignedProductService: AssignedProductService,
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private sampleDataAnalyticsService: SampleDataAnalyticsService,
    private store: Store,
  ) {}

  // eslint-disable-next-line max-lines-per-function
  public ngOnInit(): void {
    this.facade.fetchPlansIfNeeded();

    combineLatest([
      this.model$.pipe(ready),
      this.route.data,
      this.facade.getAdminsWithManageSubscriptionAbility(),
      this.facade.getInvoices(),
      this.store.select(AccountSelectors.currentCompany),
      this.store.select(BillingPlansSelectors.activePlan),
      this.assignedProductService.data$,
    ])
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(
        // eslint-disable-next-line max-lines-per-function
        ([model, data, teamMembers, invoices, currentCompany, activePlan, assignedProduct]: [
          PricingFeatureLoadedModel,
          Data,
          User[],
          InvoiceType[],
          Company,
          BillingPlan,
          CustomBillingPlan | undefined,
        ]) => {
          this.model = model;
          this.teamMembers = teamMembers;
          this.invoices = invoices;
          this.trialDaysLeft = moment(currentCompany!.trialPeriodEnd).diff(moment(), 'days', false);

          if (model.subscription?.autoCollection !== false) {
            if (activePlan || model.subscriptionStatus.trialing) {
              this.hasAssignedProduct = !!assignedProduct;
            }
          }

          if (model.loaded) {
            const { canManageSubscription } = model;
            this.showCta = canManageSubscription;
            this.showFaq = canManageSubscription;
            this.showAdminsAvatars = !canManageSubscription;

            if (this.showFaq) {
              this.commonQuestions = this.getCommonQuestions();
            }

            if (this.showCta) {
              this.cta = !this.hasAssignedProduct
                ? this.t('app.pricing.billing_locked_page.cta')
                : this.t('app.pricing.plan_picker.finish_your_order');
            }

            this.setupContent(data.reason, canManageSubscription, model);
          }

          this.cdRef.detectChanges();
        },
      );
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public goToOrder() {
    this.hasAssignedProduct
      ? this.assignedProductService.goToBilling()
      : this.facade.openPlanPicker();
  }

  public toggle(question: FaqItem) {
    if (this.expandedQuestions.has(question)) {
      this.expandedQuestions.delete(question);
    } else {
      this.expandedQuestions.add(question);
    }

    this.cdRef.detectChanges();
  }

  public trackByIndex(index: number) {
    return index;
  }

  public trackChoosePlan() {
    if ((<PricingFeatureLoadedModel>this.model).trial) {
      this.sampleDataAnalyticsService.trackChooseYourPlan(
        ChooseYourPlanContext.Billing,
        (<PricingFeatureLoadedModel>this.model).hasSampleData,
        this.trialDaysLeft,
      );
    }
  }

  public onLoadMore(): void {
    if (this.hasMoreInvoices) {
      this.facade.loadMoreInvoices();
    }
  }

  public downloadInvoicePdf(invoice: InvoiceType) {
    this.facade.downloadInvoice(invoice.id, invoice.resourceType);
  }

  private t(...args: [string, any?]) {
    return this.i18n.translate(...args);
  }

  // TODO: waiting for copy, change keys
  // eslint-disable-next-line max-lines-per-function
  private getCommonQuestions(): FaqItem[] {
    return [
      {
        questionKey: this.t('app.pricing.billing_locked_page.common_questions.item_1.header'),
        answerKey: this.t('app.pricing.billing_locked_page.common_questions.item_1.description'),
      },
      {
        questionKey: this.t('app.pricing.billing_locked_page.common_questions.item_2.header'),
        answerKey: this.t('app.pricing.billing_locked_page.common_questions.item_2.description_v2'),
      },
      {
        questionKey: this.t('app.pricing.billing_locked_page.common_questions.item_3.header_v2'),
        answerKey: this.t('app.pricing.billing_locked_page.common_questions.item_3.description_v2'),
      },
      {
        questionKey: this.t('app.pricing.billing_locked_page.common_questions.item_4.header'),
        answerKey: this.t('app.pricing.billing_locked_page.common_questions.item_4.description'),
      },
      {
        questionKey: this.t('app.pricing.billing_locked_page.common_questions.item_5.header'),
        answerKey: this.t('app.pricing.billing_locked_page.common_questions.item_5.description'),
      },
      {
        questionKey: this.t('app.pricing.billing_locked_page.common_questions.item_6.header'),
        answerKey: this.t('app.pricing.billing_locked_page.common_questions.item_6.description'),
      },
      {
        questionKey: this.t('app.pricing.billing_locked_page.common_questions.item_7.header'),
        answerKey: this.t('app.pricing.billing_locked_page.common_questions.item_7.description'),
      },
      {
        questionKey: this.t('app.pricing.billing_locked_page.common_questions.item_8.header'),
        answerKey: this.t('app.pricing.billing_locked_page.common_questions.item_8.description_v2'),
      },
      {
        questionKey: this.t('app.pricing.billing_locked_page.common_questions.item_9.header'),
        answerKey: this.t('app.pricing.billing_locked_page.common_questions.item_9.description_v2'),
      },
      {
        questionKey: this.t('app.pricing.billing_locked_page.common_questions.item_10.header'),
        answerKey: this.t(
          'app.pricing.billing_locked_page.common_questions.item_10.description_v2',
        ),
      },
      {
        questionKey: this.t('app.pricing.billing_locked_page.common_questions.item_11.header'),
        answerKey: this.t(
          'app.pricing.billing_locked_page.common_questions.item_11.description_v2',
        ),
      },
      {
        questionKey: this.t('app.pricing.billing_locked_page.common_questions.item_12.header'),
        answerKey: this.t('app.pricing.billing_locked_page.common_questions.item_12.description'),
      },
    ];
  }

  // eslint-disable-next-line max-lines-per-function
  private setupContent(
    reason: BillingLockedStateReason,
    canManageSubscription: boolean,
    model: PricingFeatureLoadedModel,
  ) {
    switch (reason) {
      case 'subscription_blocked':
        if (canManageSubscription) {
          this.imageUrl = 'assets/images/pricing/locked-page/account-upgrade.png';
          this.title = this.t('app.pricing.billing_locked_page.subscription_blocked_header');
          this.message = this.t('app.pricing.billing_locked_page.subscription_blocked_description');
        } else {
          this.imageUrl = 'assets/images/pricing/locked-page/account-frozen.png';
          this.title = this.t('app.pricing.billing_locked_page.account_frozen');
          this.message = this.t(
            'app.pricing.billing_locked_page.subscription_blocked_description_no_permission',
          );
        }
        break;

      case 'subscription_expired':
        if (canManageSubscription) {
          this.imageUrl = 'assets/images/pricing/locked-page/account-upgrade.svg';
          this.title = this.t('app.pricing.billing_locked_page.subscription_expired_header');
          this.message = this.t('app.pricing.billing_locked_page.subscription_expired_description');
        } else {
          this.imageUrl = 'assets/images/pricing/locked-page/account-frozen.svg';
          this.title = this.t('app.pricing.billing_locked_page.account_frozen');
          this.message = this.t(
            'app.pricing.billing_locked_page.subscription_expired_description_no_permission',
          );
        }
        break;

      case 'trial_active':
        this.imageUrl = 'assets/images/pricing/locked-page/account-upgrade.svg';
        const untilDate = model.trial && model.trial.untilDate ? model.trial.untilDate : moment();
        const daysLeft = diffDates(untilDate, 'days');

        if (canManageSubscription) {
          this.title = this.t('app.pricing.billing_locked_page.trial_active_header', {
            daysLeft,
          });
          this.message = this.t('app.pricing.billing_locked_page.trial_active_description');
        } else {
          this.title = this.t('app.pricing.billing_locked_page.trial_active_header_no_permission', {
            daysLeft,
          });
          this.message = this.t(
            'app.pricing.billing_locked_page.trial_active_description_no_permission',
          );
        }
        break;

      case 'trial_ended':
        this.imageUrl = 'assets/images/pricing/locked-page/account-frozen.svg';

        if (canManageSubscription) {
          this.title = this.t('app.pricing.billing_locked_page.trial_ended_header');
          this.message = this.t('app.pricing.billing_locked_page.trial_ended_description');
        } else {
          this.title = this.t('app.pricing.billing_locked_page.trial_ended_header');
          this.message = this.t(
            'app.pricing.billing_locked_page.trial_ended_description_no_permission',
          );
        }
        break;

      default:
        break;
    }
    // eslint-disable-next-line max-lines
  }
}
